import {
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  EventEmitter,
  input,
  Input,
  Output,
  TemplateRef,
} from '@angular/core';

import { UntilDestroy } from '@ngneat/until-destroy';

import { SpaListTemplateComponent } from './spa-list-template/spa-list-template.component';

@UntilDestroy()
@Component({
  selector: 'spa-list',
  templateUrl: 'spa-list.component.html',
  styleUrls: ['spa-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpaListComponent<T> {
  @Input() rows: T[] | null = null;
  readonly rowsIdsWithError = input<string[] | null>(null);
  @Input() rowsIdsWithHint: string[] | null = null;
  @Input() positionedList: PositionedList<T> | null = null;
  @Input() selectedRow: string | null = null;
  @Input() rowId: keyof T | undefined;
  @Input() interactive = false;
  readonly errorText = input('');
  @Input() hintText = '';
  showBorder = input<boolean>(true);
  @Output() selectElement = new EventEmitter<T | undefined>();
  @Output() removeElement = new EventEmitter<number | undefined>();
  templates = contentChildren(SpaListTemplateComponent);

  rowContainsHint(rowId2: T[keyof T]): boolean {
    return !!this.rowsIdsWithHint?.find((rowId1) => rowId1 === rowId2);
  }

  rowContainsError(rowId2: T[keyof T]): boolean {
    return !!this.rowsIdsWithError()?.find((rowId1) => rowId1 === rowId2);
  }

  getTemplate(key: string | number | symbol) {
    return this.templates().find((t) => t.templateKey === key);
  }

  cellValue(row: T, column: PositionedListItem<T>): string {
    const value = column.value ? column.value(row) : row[column.key as keyof T];
    return value === null || value === undefined ? '' : String(value);
  }

  onClickRow(row: T): void {
    this.selectElement.emit(row);
  }

  removeSelectedEmployee(index: number): void {
    this.removeElement.emit(index);
  }

  translationKey(column: PositionedListItem<T>) {
    if (column.translationKey) {
      return column.translationKey;
    }
    return column.key ? ['label.', column.key].join('') : null;
  }
}

export interface PositionedList<T> {
  left?: PositionedListItem<T>[];
  right?: PositionedListItem<T>[];
  bottom?: PositionedListItem<T>[];
}

export interface PositionedListItem<T> {
  key: string | keyof T;
  translationKey?: string;
  value?: (row: T) => unknown;
  size?: 'small' | 'medium' | 'large';
  template?: TemplateRef<unknown>;
}
